import { Col, Row } from 'react-grid-system';
import styled from 'styled-components';
import { useUserLanguage, useCreateCommunicationContext } from '@/customHooks';
import { useGetMassiveCommunicationTemplatesQuery } from '@/graphql/generated';
import { WriteCommunicationMessage } from './WriteCommunicationMessage';
import templateIcon from '@/assets/communication-icons/plantilla.png';
import createTemplateIcon from '@/assets/communication-icons/crear-plantilla.png';
import textsData from '@/textData/CreateCommunicationsTexts.json';

const ToggleContainer = styled(Row)`
  margin-top: 1.5rem;
  margin-bottom: 3rem;
  justify-content: center;
`;

const ToggleCard = styled.button<{ $selected: boolean }>`
  border: ${({ theme, $selected }) =>
    $selected ? `2px solid ${theme.colors.primary1}` : '2px solid transparent'};
  border-radius: ${({ theme }) => theme.border.radius.base};
  width: 180px;
  height: 180px;
  cursor: pointer;
  font-weight: 600;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.colors.light : theme.colors.background2};
  color: ${({ theme, $selected }) =>
    $selected ? theme.colors.primary1 : theme.colors.text};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  box-shadow: ${({ $selected }) =>
    $selected ? '0 6px 14px rgba(0,0,0,0.10)' : '0 4px 10px rgba(0,0,0,0.06)'};
  margin: 0 0.75rem;
`;

const TemplatesContainer = styled(Row)`
  justify-content: center;
  margin-top: 1.5rem;
`;

const TemplatesHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  h3 {
    font-size: 1.6rem;
    font-weight: 700;
  }
  p {
    font-size: 0.95rem;
    font-weight: 500;
  }
`;

const TemplateCard = styled.div<{ $selected: boolean }>`
  background: ${({ theme }) => theme.colors.light};
  border-radius: ${({ theme }) => theme.border.radius.base};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  padding: 1.5rem 2rem;
  margin-bottom: 1.5rem;
  border: 2px solid
    ${({ theme, $selected }) =>
      $selected ? theme.colors.primary1 : 'transparent'};
  cursor: pointer;
`;

export const ComposeCommunicationMessage = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;
  const templateTexts = texts.templates;
  const { state, setMode, setTitle, setMessage, setSelectedTemplate } =
    useCreateCommunicationContext();

  const { data, loading } = useGetMassiveCommunicationTemplatesQuery();

  const mode = state.mode ?? 'TEMPLATES';

  const onSelectTemplate = (id: string, title: string, content: string) => {
    setSelectedTemplate(id);
    setTitle(title);
    setMessage(content);
  };

  return (
    <>
      <ToggleContainer justify="center">
        <Col xs="content">
          <ToggleCard
            type="button"
            $selected={mode === 'TEMPLATES'}
            onClick={() => setMode('TEMPLATES')}
          >
            <img src={templateIcon} alt="" style={{ width: 60, height: 60 }} />
            <span>{templateTexts.toggleTemplates}</span>
          </ToggleCard>
        </Col>
        <Col xs="content">
          <ToggleCard
            type="button"
            $selected={mode === 'NEW'}
            onClick={() => setMode('NEW')}
          >
            <img src={createTemplateIcon} alt="" style={{ width: 60, height: 60 }} />
            <span>{templateTexts.toggleNew}</span>
          </ToggleCard>
        </Col>
      </ToggleContainer>

      {mode === 'TEMPLATES' ? (
        <TemplatesContainer>
          <Col xs={12}>
            <TemplatesHeader>
              <h3 style={{ marginBottom: '0.5rem' }}>{templateTexts.header}</h3>
              <p style={{ marginBottom: 0, color: '#666' }}>{templateTexts.subtitle}</p>
            </TemplatesHeader>
          </Col>
          <Col md={15} className="px-4 md:px-0">
            <div className="mx-auto w-full max-w-4xl">
              {loading && <p>{templateTexts.loading}</p>}
              {!loading && (!data || data.massiveCommunicationTemplates.length === 0) && (
                <p>{templateTexts.empty}</p>
              )}
              {!loading &&
                data?.massiveCommunicationTemplates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    $selected={state.selectedTemplateId === template.id}
                    onClick={() =>
                      onSelectTemplate(template.id, template.title, template.content)
                    }
                  >
                    <p>
                      <strong>{templateTexts.subjectLabel}</strong>{' '}
                      {template.title}
                    </p>
                    <p style={{ marginTop: '0.5rem' }}>
                      <strong>{templateTexts.messageLabel}</strong>{' '}
                      {template.content}
                    </p>
                  </TemplateCard>
                ))}
            </div>
          </Col>
        </TemplatesContainer>
      ) : (
        <WriteCommunicationMessage />
      )}
    </>
  );
};

